/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable @typescript-eslint/no-unused-vars */
declare function require(path: string): any;
import { IInvestmentActivity } from "@common/models/IInvestmentActivity";
import { IPCCommitment } from "../models/IPCCommitment";
import { IPortfolioCompany } from "../models/IPortfolioCompany";
import { ICapitalCall } from "@common/models/ICapitalCall";
import { DateTimeReviver } from "./DateHelper";
import { CommitmentState } from "@common/enums/commitment/CommitmentState";
import { ICapitalMember } from "@common/models/ICapitalMember";
import { CapitalCallType } from "../enums/capitalCall/CapitalCallType";
import { CapitalMemberType } from "@common/enums/CapitalMemberType";
import { IValuation } from "@common/models/IValuation";
import { ValuationState } from "@common/enums/valuation/ValuationState";
import { IDistribution } from "@common/models/IDistribution";
import { CapitalCallState } from "@common/enums/capitalCall/CapitalCallState";
import { DistributionState } from "@common/enums/distribution/DistributionState";
import { FundFamilyMainFundMember, FundFamilyName } from "@common/enums/FundFamilyName";
var Finance = require('./FinanceService.js')
var finance = new Finance();

class CompanyRecalcService {

    constructor(private company: IPortfolioCompany) {
        this.recalculate();
    }
    
    /**
     * 
     * @returns The currently active commitments, or null if there is no active commitment.
     * 
     * Logic: The most recent commitment with a state of 'Approved' is the active commitment.
     */
    activeCommitments(): IPCCommitment | null {
        if (this.company) {
            if (this.company.commitments) {
                const todaysDate = new Date();
                let commitments = this.company.commitments.filter(c => new Date(c?.effectiveDate) <= todaysDate);
                commitments = commitments.filter(c => c.state === CommitmentState.Approved).sort((a, b) => a?.effectiveDate < b?.effectiveDate ? 1 : -1);
                return commitments.length > 0 ? commitments[0] : null;
            }
        }

        return null;
    }

    /**
     * 
     * @returns sharing ratio for a given fund-level captial member as a ratio
     * of all quantum commitments (excludes management and non-quantum investors).
     * This uses the actuals from the capitalMember records, not the commitments.
     * The recaclulate method will fall back to commitment ratios, if no actuals
     * exist (i.e. historical CCs without cap table)
     */
    fundRatio(memberName: string, asOfDate?: Date): number {
        if (!this.company.capitalMembers) return 0;
        const fundCreatedDate = this.fundCreatedDate(memberName);

        const member = this.company.capitalMembers.find(
            m => m.name?.toLowerCase() === memberName.toLowerCase()
        );
        if (!member) return 0;
        const totalCommitment = this.totalCommitments();
        const lastFundedCommitment = this.activeCommitments();
        let committedMemberFund = 0;
        //OLD LOGIC: use fundedToDate for specific member types if splitLogicEnabled is false
        if (!this.company?.splitLogicEnabled) {
            return this.fundRatioSplitDisabled(memberName)
        }
        const totalContribution = this.totalContributions();
        const lastFundedCapCall = this.lastFundedCapCall();
        if(lastFundedCapCall?.capitalContributions && lastFundedCapCall.capitalContributions.length > 0){
            const totalFundedToDate = this.fundedWiseContribution(memberName);
            return totalFundedToDate > 0 ? totalFundedToDate / totalContribution : 0;
        }else{
            //For historic commitments find the funds which was created as of the lastfundeddate
            const contributionAvailableForFund = this.company.investedCapital?.filter(c => c.state === CapitalCallState.Funded && c.capitalCallType === CapitalCallType.EquityCall && new Date(c.fundingDate) >= new Date(fundCreatedDate)).length > 0;
            const filteredCommitments = this.company.commitments
            .filter(c => asOfDate ?  c.effectiveDate <=   asOfDate : c.effectiveDate <= fundCreatedDate)
            .sort((a, b) => a.effectiveDate < b.effectiveDate ? 1 : -1)
            const lastCreatedFundMembers  = filteredCommitments[0]?.members.map(m => m.name) || [];
           if (lastCreatedFundMembers.includes(memberName) && lastFundedCommitment && lastFundedCommitment.members && contributionAvailableForFund) {
                    lastFundedCommitment.members.forEach(member => {
                        if (member.name.toLocaleLowerCase() === memberName.toLocaleLowerCase()) {
                            committedMemberFund = member.amount;
                        }
                    })
                return committedMemberFund / totalCommitment;
                }else{
                    return 0;
                }

                
            
        }


    }
    
    

    quantumRatio():number { 
        if (!this.company.capitalMembers) {
            return 0;
        }
        const totalFundedToDate = this.company.capitalMembers.filter(({commitment}) => commitment).reduce((acc, cur) => acc + cur.fundedToDate, 0);
        const quantumCapitalMembers = this.company.capitalMembers.filter(m => m.memberType != CapitalMemberType.Management);
        const quantumFundedToDate = quantumCapitalMembers.reduce((total, member) => { return total + member.fundedToDate }, 0);
        return quantumFundedToDate / totalFundedToDate;

    }

    /**
     * Calculates the distribution ratio for a given capital member.
     *
     * The ratio represents the member's share of total distributions
     * as of a specific date.
     *
     * Logic:
     * 1. If the latest funded distribution contains explicit
     *    distributionContributions, the ratio is calculated as:
     *       (member's total funded distributions) / (total distributions)
     *
     * 2. If no contribution split exists, the ratio is derived from
     *    the most recent approved commitment (effective before or on asOfDate):
     *       (member commitment amount) / (total commitment amount)
     *
     * Returns 0 if:
     * - Capital members are not defined
     * - Member is not found
     * - No valid commitments/distributions exist
     *
     * @param memberName - Name of the capital member
     * @param asOfDate - Optional cutoff date for calculation
     * @returns Distribution ratio (0 to 1)
     */
    fundDistributionRatio(memberName: string, asOfDate?: Date): number {
        if (!this.company.capitalMembers) return 0;

        const member = this.company.capitalMembers.find(
            m => m.name?.toLowerCase() === memberName.toLowerCase()
        );
        if (!member) return 0;
        const totalDistributions = this.totalDistributions();
        const lastFundedDistribution = this.lastFundedDistribution();
        const lastFundedCapCall = this.lastFundedCapCall();
        if(lastFundedDistribution?.distributionContributions && lastFundedDistribution.distributionContributions.length > 0){
            const totalDistributedToDate = this.fundedWiseDistribution(memberName);
            return totalDistributedToDate > 0 ? totalDistributedToDate / totalDistributions : 0;
        }else if(lastFundedCapCall){
            return this.fundRatio(memberName, asOfDate);
        }else{
            let committedMemberFund = 0;
            let totalCommitment = 0;
           const lastFundedCommitment = this.company.commitments?.filter(c => c.state === CommitmentState.Approved && c.effectiveDate <= asOfDate).sort((a, b) => a.effectiveDate < b.effectiveDate ? 1 : -1);
            const lastCreatedFundMembers  = lastFundedCommitment[0]?.members.map(m => m.name) || [];
           if (lastCreatedFundMembers.includes(memberName) && lastFundedCommitment && lastFundedCommitment[0].members) {
                    lastFundedCommitment[0].members.forEach(member => {
                        if (member.name.toLocaleLowerCase() === memberName.toLocaleLowerCase()) {
                            committedMemberFund = member.amount;
                        }
                        totalCommitment += member.amount;
                    })
                if (totalCommitment > 0) {
                    return committedMemberFund / totalCommitment;
                }

                const ratio = this.fundRatio(memberName, asOfDate);

            if (Number.isFinite(ratio)) {
                return ratio;
            }

            return 0;
                }else{
                    return 0;
                }
        }


    }


    mainFundMember(fundFamily: string): string {
        switch (fundFamily) {
            case FundFamilyName.V:
                return FundFamilyMainFundMember.QEPV;
            case FundFamilyName.VI:
                return FundFamilyMainFundMember.QEPVI;
            case FundFamilyName.VII:
                return FundFamilyMainFundMember.QEPVII;
            case FundFamilyName.VIII:
                return FundFamilyMainFundMember.QEPVIII;
            case FundFamilyName.QIFI:
                return FundFamilyMainFundMember.QEPVII;
            case FundFamilyName.QIFII:
                return FundFamilyMainFundMember.QEPVIII;
            case FundFamilyName.QCSI:
                return FundFamilyMainFundMember.QLCPI;
            case FundFamilyName.QCSII:
                return FundFamilyMainFundMember.QCSII;
            case FundFamilyMainFundMember.QEPIX:
                return FundFamilyMainFundMember.QEPIX;
            case FundFamilyName.FCV:
                return FundFamilyName.FCV; 
            default:
                return '';
        }
    }

    fundRatioSplitDisabled(memberName: string){
        const eligible = this.company.capitalMembers.filter(m =>
            [CapitalMemberType.MainFund, CapitalMemberType.Coinvestment, CapitalMemberType.DirectCoinvestment].includes(m.memberType!)
        );

        const totalCommitment = this.totalCommitments();
        const lastFundedCommitment = this.activeCommitments();

        const member = this.company.capitalMembers.find(
            m => m.name?.toLowerCase() === memberName.toLowerCase()
        );

        let committedMemberFund = 0;

        const totalFunded = eligible.reduce((sum, m) => sum + (m.fundedToDate || 0), 0);

        if (!member) return 0;

        if (totalFunded > 0 && (member.fundedToDate ?? 0) > 0) {
            return (member.fundedToDate ?? 0) / totalFunded;
        }

        if (totalFunded === 0 && lastFundedCommitment?.members) {
            lastFundedCommitment.members.forEach(m => {
                if (m.name?.toLocaleLowerCase() === memberName.toLocaleLowerCase()) {
                    committedMemberFund = m.amount;
                }
            });
        }

        return committedMemberFund / totalCommitment;
    }

    /**
     * 
     * @returns returns Fund level contribution from capitalization table.
     */

    fundedWiseContribution(fundName: string): number {
        const lastFundedCapCall = this.company.investedCapital.filter(cc=>(cc.state === CapitalCallState.Funded && cc.capitalCallType == CapitalCallType.EquityCall)).sort((a, b) => new Date(b.fundingDate).getTime() - new Date(a.fundingDate).getTime())[0];
        return lastFundedCapCall
            ?.capitalContributions
            .filter(c => {
            if (!fundName) return true;

            const members = Array.isArray(c.member) ? c.member : [c.member];
            return members?.some(m => m?.name === fundName);
            })
            .reduce((sum, c) => sum + (c.amount+c.member.fundedToDate), 0);
    }

        /**
     * 
     * @returns returns Fund level contribution from capitalization table.
     */

    fundedWiseDistribution(fundName?: string): number {
        return this.lastFundedDistribution()
            ?.distributionContributions
            .filter(c => {
            if (!fundName) return true;

            const members = Array.isArray(c.member) ? c.member : [c.member];
            return members?.some(m => m?.name === fundName);
            })
            .reduce((sum, c) => sum + (c.amount+c.member.distributedToDate), 0);
    }

       /**
     * 
     * @returns returns Fund level contribution if capitalization table is available, 
     * if capitalization table is not available it will calculate ratio of the fund
     *  from totalcontributions.
     */

    fundWiseContributionByDate(fundingDate: Date, fundName?: string): number {
         const fundCreatedDate = this.fundCreatedDate(fundName);
         let amountForFund = 0;
         const ratio = this.fundRatio(fundName,fundCreatedDate)
         const latestContribution = (this.company.investedCapital || [])
            .filter(c => c.state === CapitalCallState.Funded && c.capitalCallType === CapitalCallType.EquityCall && c.fundingDate >= fundCreatedDate && c.fundingDate <= fundingDate)
            .reduce((latest, current) => {
                if (!latest) return current;
                return new Date(current.fundingDate) > new Date(latest.fundingDate)
            ? current
            : latest;
            }, null);
        if(!latestContribution) return 0;
        if(latestContribution?.capitalContributions && latestContribution.capitalContributions.length > 0){
            amountForFund = latestContribution.capitalContributions.reduce((sum, contrib) => {
                if (!contrib || !contrib.member) return sum;
                const members = Array.isArray(contrib.member) ? contrib.member : [contrib.member];
                for (const m of members) {
                    if (m?.name && m.name.toLowerCase() === fundName.toLowerCase()) {
                        return sum + (contrib.member.fundedToDate +  contrib.amount || 0);
                    }
                }
                return sum;
            }, 0);
            }else{
                amountForFund = this.totalContributions() * ratio;
         }

                return amountForFund;
    }

    
    /**
     * 
     * @returns returns Fund level distribution.
     */

    fundWiseDistributionByDate(fundingDate?: Date, fundName?: string): number {
        const fundCreatedDate = this.fundCreatedDate(fundName);
        const ratio = this.fundDistributionRatio(fundName,fundCreatedDate)
        let amountForFund = 0;
        const latestDistribution = (this.company.distributions || [])
        .filter(c => c.state === DistributionState.Funded && c.fundingDate >= fundCreatedDate && c.fundingDate <= fundingDate)
        .reduce((latest, current) => {
            if (!latest) return current;
            return new Date(current.fundingDate) > new Date(latest.fundingDate)
        ? current
        : latest;
        }, null);
        if(!latestDistribution) return 0;
        if(latestDistribution?.distributionContributions && latestDistribution.distributionContributions.length > 0){
            amountForFund = latestDistribution.distributionContributions.reduce((sum, contrib) => {
                if (!contrib || !contrib.member) return sum;
                const members = Array.isArray(contrib.member) ? contrib.member : [contrib.member];
                for (const m of members) {
                    if (m?.name && m.name.toLowerCase() === fundName.toLowerCase()) {
                        return sum + (contrib.member.distributedToDate +  contrib.amount || 0);
                    }
                }
                return sum;
            }, 0);
            }else{
                amountForFund = this.totalDistributions() * ratio;
         }

                return amountForFund;
    }

    /**
     *  Is there an unapproved commitment to this company?
     */
    hasPendingCommitment(): boolean {
        if (this.company) {
            if (this.company.commitments) {
                return this.company.commitments.filter(c => c.state !== CommitmentState.Approved).length > 0;
            }
        }

        return false;
    }

    /**
     * 
     * @returns True if there is a capital call that is not in the 'Funded' state.
     */
    hasPendingCapitalCall(): boolean {
        if (this.company) {
            if (this.company.investedCapital) {
                return this.company.investedCapital.filter(c => c.state !== CapitalCallState.Funded && c.state !== CapitalCallState.Repaid).length > 0;
            }
        }

        return false;
    }

    /**
     * 
     * @returns True if there is a distribution that is not in the 'Funded' state.
     */
    hasPendingDistribution(): boolean {
        if (this.company) {
            if (this.company.distributions) {   
                return this.company.distributions.filter(c => c.state !== DistributionState.Funded).length > 0;
            }
        }

        return false;
    }

    /**
     * 
     * @returns the holding or residual value of the company, which is the total
     * value that is entered, less all distributions.
     */
    holdingValue(): number {
        if (this.company) {
            if (this.company.valuation && this.company.valuation.length > 0) {
                // Find the most recent valuation record 
                const holding = this.company.valuation.sort((a, b) => a.effectiveDate < b.effectiveDate ? 1 : -1)[0];
                if (holding) {
                    return (holding.holdingValue || 0);
                }
            }
        }

        // If there are no valuation records, return the total contributions less the 
        // total distributions.
        return this.totalContributions() - this.totalDistributions();
    }

    /**
     * 
     * @returns the holding or residual value of the company, which is the total
     * value that is entered, less all distributions.
     */
    enterpriseValue(): number {
        if (this.company) {
            if (this.company.valuation && this.company.valuation.length > 0) {
                // Find the most recent valuation record 
                const valuation = this.company.valuation.sort((a, b) => a.effectiveDate < b.effectiveDate ? 1 : -1)[0];
                if (valuation) {
                    return (valuation.enterpriseValue || null);
                }
            }
        }

        // If there are no valuation records, return the total contributions less the 
        // total distributions.
        return null;
    }

    /**
     * 
     * @returns The date of the first investment in the company, typically the date of the first capital call.
     */
    investmentDate(): Date {
        return this.company?.investedCapital?.reduce((a, b) => a < b.fundingDate ? a : b.fundingDate, new Date());
    }

    /**
     */
    get investmentStatus(): string {
        if (this.totalDistributions() > 0) {
            if (this.holdingValue() > 0)
                return "Partially Realized";
            else {
                return "Fully Realized";
            }
        }
        else {
            if (this.holdingValue() > 0) {
                return "Unrealized";
            }
            else {
                if (this.totalContributions() > 0) {
                    return "Commitment Withdrawn";
                }
                return "No Investment";
            }
        }
    }
    /**
     * @returns True if the user is assigned to the company in any role.
     */
    isAssignedToMe(email: string): boolean {
        if (!this.company.team) {
            return false;
        }
        email = email.toLowerCase();
        const allUsers: string[] = [ 
            ...this.company.team?.boardMembers || [],
            ...this.company.team?.executionTeam || [],
            ...this.company.team?.technicalAdvisors || [],
            ...this.company.team?.oversightPartners || []           
        ].map(u => u.toLowerCase()) ;

        return allUsers.includes(email);
    }    

    latestActivity(activities: IInvestmentActivity[]): IInvestmentActivity | null {
        if (activities?.length > 0) {
            return activities.sort((a, b) => a.requestedDate < b.requestedDate ? 1 : -1)[0];
        } 

        return null;
    }

    /**
     * 
     * @returns returns sharing ratio for a specific member as a ratio of all
     */
     memberRatio(memberName: string): number {
        const totalFundedToDate = this.company.capitalMembers.reduce((total, member) => { return total + member.fundedToDate }, 0);;
        const capitalMember = this.company.capitalMembers?.find(m => m.name.toLocaleLowerCase() === memberName.toLocaleLowerCase());
        return capitalMember?.fundedToDate / totalFundedToDate;
    }

    /**
     * 
     * @returns the Mulitiple Of Invested Capital for the company.
     */
    moic(): number {
        const moic = this.totalValue() / this.totalContributions()
        return Number.isFinite(moic)? moic:0;
    }

    /**
     * Calculates the MOIC (Multiple of Invested Capital) for a given fund
     * as of a specific funding date.
     *
     * Formula:
     *   MOIC = (Current Holding Value allocated to fund + 
     *           Total Distributed To Date) 
     *          / Total Funded To Date
     *
     * Where:
     * - Holding value is allocated using the fund ratio
     * - Distributed value is calculated up to the given funding date
     * - Funded value represents total capital contributed up to the date
     *
     * Returns 0 if the fund has no distribution.
     *
     * @param fundName - Name of the fund
     * @param fundingDate - Cutoff date for contribution and distribution calculations
     * @returns MOIC value (multiple of invested capital)
     */
    fundMoic(fundName:string, fundingDate:Date): number {
        const totalDistributedToDate = this.fundWiseDistributionByDate(fundingDate,fundName);
        const totalFundedToDate = this.fundWiseContributionByDate(fundingDate,fundName);
        const fundRatio = this.fundRatio(fundName);
        return totalFundedToDate > 0 ? (((fundRatio * this.holdingValue()) + totalDistributedToDate) / totalFundedToDate) : 0;
    }

    /**
     * Factory method to create a new CompanyRecalcService with the company object
     * trimmed to the specified date.
     * 
     * @param company Full company object
     * @param asOfDate Cutoff date for trimming
     * @returns A new CompanyRecalcService with the company object trimmed to the
     *          specified date.
     */
    static newTrimmedCalculator(company: IPortfolioCompany, asOfDate: Date=new Date(),updateCapitalmembers:boolean=true): CompanyRecalcService {
        const newCompany: IPortfolioCompany = JSON.parse(JSON.stringify(company || {}), DateTimeReviver);
        // Normalize to end of the asOfDate calendar day in UTC so that dates stored with a local-timezone
        // offset (e.g. T05:00Z for a US user entering midnight local time) are still included when
        // the cutoff date was stored as UTC midnight (T00:00Z).
        const endOfAsOfDate = new Date(Date.UTC(asOfDate.getUTCFullYear(), asOfDate.getUTCMonth(), asOfDate.getUTCDate(), 23, 59, 59, 999));
        newCompany.commitments = newCompany.commitments?.filter(c => c.state === CommitmentState.Approved && c.effectiveDate <= endOfAsOfDate) || [];
        newCompany.investedCapital = newCompany.investedCapital?.filter(c => c.state === CapitalCallState.Funded && c.fundingDate <= endOfAsOfDate) || [];
        newCompany.distributions = newCompany.distributions?.filter(c => c.state === DistributionState.Funded && c.fundingDate <= endOfAsOfDate) || [];
        newCompany.valuation = newCompany.valuation?.filter(c => checkValuationState(c,asOfDate)) || [];
        newCompany.capitalMembers = updateCapitalmembers? [] : newCompany.capitalMembers;
        const retval= new CompanyRecalcService(newCompany);
        retval.recalculate();
        return retval;
    }

        /**
     * Factory method to create a new CompanyRecalcService with the company object
     * trimmed to the specified startdate and end date.
     * 
     * @param company Full company object
     * @param startDate start date for trimming
     * @param endDate end date for trimming
     * @param fund fund value of the company
     * @returns A new CompanyRecalcService with the company object trimmed to the
     *          specified date.
     */
    static fundwiseTrimmedCalculator(company: IPortfolioCompany,startDate:Date = new Date(), endDate: Date=new Date(),fund?:string): CompanyRecalcService {
        if (!company) return new CompanyRecalcService({} as IPortfolioCompany);
        const newCompany: IPortfolioCompany = structuredClone(company);
        // Calculate fund ratio if applicable
        const ratio = fund ? new CompanyRecalcService(company).fundRatio(fund) : 1;

        // Helper function for filtering by date range
        const isWithinRange = (item: { effectiveDate?: Date; fundingDate?: Date }) => 
        (item.effectiveDate ? item.effectiveDate >= startDate && item.effectiveDate <= endDate : false) ||
        (item.fundingDate ? item.fundingDate >= startDate && item.fundingDate <= endDate : false);


        // Filter commitments, investedCapital, and distributions in a single step
        newCompany.commitments = newCompany.commitments?.filter(c => c.state === CommitmentState.Approved && isWithinRange(c)) || [];
        newCompany.investedCapital = newCompany.investedCapital?.filter(c => c.state === CapitalCallState.Funded && isWithinRange(c)).map(c => ({
        ...c,
        amountQuantum: c.amountQuantum * ratio
        })) || [];
        newCompany.distributions = newCompany.distributions?.filter(c => c.state === DistributionState.Funded && isWithinRange(c)).map(c => ({
        ...c,
        amountQuantum: c.amountQuantum * ratio
        })) || [];

        // Filter valuations with checkValuationState
        newCompany.valuation = newCompany.valuation?.filter(c => checkValuationState(c, endDate)) || [];

        // Create recalculation service and trigger recalculation
        const retval = new CompanyRecalcService(newCompany);
        retval.recalculate();
        return retval;
    }

    /***
     * Ensures that any company data that is derived from investment activity is 
     * up to date.
     */
    recalculate() : void {  

        if (! this.company) return;

        if (! this.company.commitments) {
            return;
        }

        // Make sure the array exists, at least, so we can add to it.
        if (!this.company.capitalMembers) {
            this.company.capitalMembers = [];
        }

        // Make sure commitments are fully calculated
        this.company.commitments?.forEach(this.summarizeCommitment.bind(this));

        // Get latest approved commitment and update capital members 
        const lastApprovedCommitment = this.activeCommitments();
        if (lastApprovedCommitment){
            this.updateMembersFromCommitment(lastApprovedCommitment);
        }

        const totalDistributions = this.totalDistributions();

        // get latest capitalCall and update capitalMembers
        // if contributions is available fetch sharing ratio from contributions 
        // else calculate from commitments
        const lastFundedCapitalCall = this.lastFundedCapCall();
        /**
         * This is important. Some capital calls do not upload a good cap table,
         * so nothing is parsed. Other edge cases are that the associate enters
         * cap table data at Quantum level, not fund level.
         */
        if(lastFundedCapitalCall?.capitalContributions){
            if (lastFundedCapitalCall.capitalContributions.length > 0) {
                const totalFundedToDate = lastFundedCapitalCall.capitalContributions.reduce((total, member) => { return total + member.member.fundedToDate + member.amount }, 0)
                for (const contribution of lastFundedCapitalCall.capitalContributions) {
                    const capitalMember = this.company.capitalMembers?.find(m => m.name == contribution.member.name);
                    const fundedToDate = contribution.member.fundedToDate + contribution.amount;
                    if (capitalMember) { // This means that capital member exists at the top level
                        capitalMember.fundedToDate = fundedToDate;  
                        capitalMember.percentage = fundedToDate/totalFundedToDate;
                        // If the capital member is management, we want to update the commitment as well
                        if(contribution.member.memberType == CapitalMemberType.Management){
                            capitalMember.commitment = contribution.member.commitment;
                        }
                    }else if(contribution.member.memberType == CapitalMemberType.Management) {
                        // If it does not exist at the top level, we do not want to create it unless
                        // it is management. It will be unlikely that we are missing a fund member, because
                        // add a record in a previous step based on the commitment members.
                        this.company.capitalMembers.push(
                            {
                                name: contribution.member.name,
                                memberType: contribution.member.memberType,
                                percentage: fundedToDate/totalFundedToDate ,
                                commitment: contribution.member.commitment,
                                fundedToDate: fundedToDate
                            }
                        );
                    }
                }
            }else{
                  let updatedCapMemberArray :ICapitalMember[] = [];
                  this.company.capitalMembers.forEach(capMember => {
                      if(capMember.name){
                          const updatedCapMember = {...capMember};
                          const ratio = this.fundRatio(capMember.name);
                          updatedCapMember.percentage = ratio
                          //fundedToDate is to be updated only from capital contributions
                          //This is to prevent non funded funds to be updated
                          if(!this.company?.splitLogicEnabled){
                             updatedCapMember.fundedToDate = this.totalContributions() * ratio;
                             updatedCapMember.distributedToDate = totalDistributions * ratio;
                          }else{
                             updatedCapMember.fundedToDate = this.totalContributions() * ratio;
                          }
                        
                          updatedCapMemberArray.push(updatedCapMember)                                           
                      }
                  });
                  this.company.capitalMembers = updatedCapMemberArray;
            }    
        } 
        this.updateDistributedToDate()
        if(!this.company?.splitLogicEnabled){
            
        /**
         * Override values for funds that are still being raised.
         */
        //if (lastApprovedCommitment?.effectiveDate >= lastFundedCapitalCall?.fundingDate) {
            // TODO: This is currently hard-coded, but needs to changed to use some sort of
            // status on the fund, to say if it is still raising or not.
            const fundFamilyList: string[] = [FundFamilyName.VIII, FundFamilyName.QIFII, FundFamilyName.QCSII];
            if (fundFamilyList.includes(this.company.fundFamily[0])) {
                this.recalculateFromBespokeRatios();
          //  }
         }
        }
    }

  /**
     * When we have bespoke ratios between funds, these take precedence over
     * the calculated ratios.
     */
        private recalculateFromBespokeRatios(): void {
            const fundMembers = this.company.capitalMembers?.filter(m => m.memberType === CapitalMemberType.MainFund || m.memberType === CapitalMemberType.Coinvestment);
            const totalFundedToDate = this.totalContributions();
            const totalCommitment = this.totalCommitments();
            const totalDistributions = this.totalDistributions();
            fundMembers?.forEach(member => {
                const ratio = member.commitment / totalCommitment;
                if (ratio > 0) {
                    member.percentage = ratio;
                    member.fundedToDate = member.percentage * totalFundedToDate;
                    member.commitment = member.percentage * totalCommitment;
                    member.distributedToDate = member.percentage * totalDistributions;
                }
            });

    }


      /**
     * 
     * @returns returns last Funded contribution.
     */

    lastFundedCapCall(): ICapitalCall | null {
        if (this.company) {
            if (this.company.commitments) {
                return this.company.investedCapital.filter(cc=>(cc.state === CapitalCallState.Funded && cc.capitalCallType == CapitalCallType.EquityCall)).sort((a, b) => new Date(b.fundingDate).getTime() - new Date(a.fundingDate).getTime())[0];
            }
        }
        
    }

    /**
     * Updates the distributedToDate value for each capital member
     * based on the most recent funded distribution.
     *
     * Logic:
     * - Retrieves the last funded distribution.
     * - If the distribution contains explicit distributionContributions:
     *     → Updates each matching capital member using the contributed amount.
     * - If no contribution split exists:
     *     → Allocates total distributions proportionally using
     *       the distribution ratio as of the funding date.
     *
     * This ensures capitalMembers reflect the latest cumulative
     * distributed amount after the most recent funding event.
     */
    private updateDistributedToDate(): void {
        const distributions = this.lastFundedDistribution();
        const lastFundedCapitalCall = this.lastFundedCapCall();
        if(distributions?.distributionContributions && distributions.distributionContributions.length > 0){
            distributions.distributionContributions.forEach(distContribution => {
                const capitalMember = this.company.capitalMembers?.find(m => m.name == distContribution.member.name);
                if (capitalMember) { // This means that capital member exists at the top level
                    capitalMember.distributedToDate = distContribution.member.distributedToDate + distContribution.amount;
                }else if(distContribution.member.memberType == CapitalMemberType.Management) {
                        // If it does not exist at the top level, we do not want to create it unless
                        // it is management. It will be unlikely that we are missing a fund member, because
                        // add a record in a previous step based on the commitment members.
                        const totalFundedToDate = lastFundedCapitalCall.capitalContributions.reduce((total, member) => { return total + member.member.fundedToDate + member.amount }, 0)
                        const percentage = distContribution.member?.fundedToDate ?
                            distContribution.member.fundedToDate / totalFundedToDate : 0;
                        this.company.capitalMembers.push(
                            {
                                name: distContribution.member.name,
                                memberType: distContribution.member.memberType,
                                percentage: percentage,
                                commitment: distContribution.member.commitment,
                                fundedToDate: distContribution.member?.fundedToDate || 0,
                                distributedToDate: distContribution.member.distributedToDate + distContribution.amount
                            }
                        );
                    }
            });
        }else{
            this.company.capitalMembers.forEach(capMember => {
                const ratio = this.fundDistributionRatio(capMember.name,distributions?.fundingDate);
                capMember.distributedToDate = distributions && ratio ? this.totalDistributions() * ratio : 0;
            });

        }

    }

    /**
     * 
     * @returns returns last Funded Distribution.
    */

    lastFundedDistribution(): IDistribution | null {
        if (this.company) {
            if (this.company.distributions) {
                return this.company.distributions.filter(cc=>(cc.state === DistributionState.Funded)).sort((a, b) => new Date(b.fundingDate).getTime() - new Date(a.fundingDate).getTime())[0];
            }
        }
        
    }


    // Sum up commitment values to a total for all tranches, for each member
    private summarizeCommitment(commitment: IPCCommitment): void {
        if(commitment?.members){
            commitment.members.forEach(m => {
                if (m.tranches && m.tranches.length>0) {
                    m.amount = m.tranches.reduce((a, b) => a + b.amount, 0);
                }
            });
            commitment.amount = commitment.members.reduce((a, b) => a + (b.tranches?.length ? b.tranches.reduce((c,d) => c+ d?.amount, 0) : b?.amount) , 0);
        }
    }

    /**
     * 
     * @returns returns availability.
     */
    totalAvailability(): number {
        const availability = this.totalCommitments() - this.totalContributions();
        return (availability>-0.04 && availability<0.04) ? 0.00:availability;
    }

    /**
     * 
     * @returns Total dollar value of Quantum Commitments to the company.
     */
    totalCommitments(): number {
        const activeCommitments = this.activeCommitments();
        if (activeCommitments) {
            const value = activeCommitments.members.reduce((a, b) => a + (b.tranches?.length ? b.tranches.reduce((c,d) => c+ d.amount, 0) : b.amount) , 0);
            return value;
        }

        return 0.;
    }

    /**
     * Returns the contractual committed dollar amount for a specific fund member,
     * read directly from the most recent approved commitment record.
     *
     * This is intentionally different from fundRatio() × totalCommitments().
     * fundRatio() is based on the funded-to-date share (actual capital call amounts),
     * which diverges from the commitment ratio when older funds have deployed a larger
     * proportion of capital than their committed share (e.g. QEP VIII deployed 55.6%
     * but only committed 42%). Using fundRatio for totalCommitment would produce an
     * inflated/deflated figure that does not match the commitment record.
     *
     * Logic:
     * 1. Get the most recent approved commitment as of the current date (or the
     *    trimmed asOfDate if called from a trimmed CompanyRecalcService).
     * 2. Find the member entry by name (case-insensitive).
     * 3. If the member has tranches (split commitments), sum the tranche amounts.
     *    Otherwise return the flat member amount.
     * 4. Return 0 if no active commitment exists or the member is not found.
     *
     * @param memberName - Name of the fund capital member (e.g. "QEP VIII")
     * @returns The committed dollar amount for that fund, or 0 if not found.
     */
    fundCommitmentAmount(memberName: string): number {
        // Step 1: Get the most recent approved commitment record.
        const activeCommitments = this.activeCommitments();
        if (!activeCommitments) return 0;

        // Step 2: Find this fund's entry in the commitment's members array.
        const member = activeCommitments.members?.find(m => m.name?.toLowerCase() === memberName.toLowerCase());
        if (!member) return 0;

        // Step 3: If the commitment was split into tranches, sum the tranche amounts.
        // Otherwise use the flat member.amount from the commitment record.
        return member.tranches?.length
            ? member.tranches.reduce((c, d) => c + d.amount, 0)
            : (member.amount ?? 0);
    }

    /**
     *
     * @returns Total dollar value of Invested Capital to the company to-date.
     */
    totalContributions(): number {
        return this.company ? (this.company.investedCapital || []).filter((cc)=>{return cc.state== CapitalCallState.Funded && cc.capitalCallType==CapitalCallType.EquityCall}).reduce((acc, cur) => acc + cur.amountQuantum, 0) : 0;
    }
    totalBridgeLoan(): number {
        return this.company ? (this.company.investedCapital || []).filter((cc)=>{return cc.state==CapitalCallState.Funded && cc.capitalCallType==CapitalCallType.BridgeLoan}).reduce((acc, cur) => acc + cur.amountQuantum, 0) : 0;
    }
    totalLetterOfCredit(): number {
        return this.company ? (this.company.investedCapital || []).filter((cc)=>{return cc.state==CapitalCallState.Funded && cc.capitalCallType==CapitalCallType.LetterOfCredit}).reduce((acc, cur) => acc + cur.amountQuantum, 0) : 0;
    }
    /**
     * 
     * @returns Total dollar value of Distributions from the company to-date.
     */
    totalDistributions(): number {
        return this.company ? (this.company.distributions || []).filter((dist)=>{return dist.state==DistributionState.Funded}).reduce((acc, cur) => acc + (cur.amountQuantum || 0), 0) : 0;
    }

    hasDistributions(): boolean {
        return this.company ? (this.company.distributions || []).some(dist => dist.state === DistributionState.Funded) : false;
    }

    /**
     * 
     * @returns Total dollar value of the company, including distributions and
     * holding value. This is entered value from the deal team, not calculated
     * unless there is insufficient history.
     */
    totalValue(): number {
        if (this.company) {
            if (this.company.valuation && this.company.valuation.length > 0) {
                // Find the most recent valuation record 
                const holding = this.company.valuation.sort((a, b) => a.effectiveDate < b.effectiveDate ? 1 : -1)[0];
                if (holding) {
                    return (holding.holdingValue + this.totalDistributions());
                }
            }
        }

        // If there are no valuation records, return the total contributions less the 
        // total distributions.
        return this.totalContributions() - this.totalDistributions();
    }

    /**
     * Update a capital member according to its commitment data
     * @param commitmentMember the capital member in the commitment record
     */
    private updateMembersFromCommitment(commitment: IPCCommitment) : void {
        for (const commitmentMember of commitment.members || []) {
        // Find the matching capital member
            const capitalMember : ICapitalMember = this.company?.capitalMembers?.find(m => m.name === commitmentMember.name);
           
            if(!capitalMember){
                // It is is not found, create it and give it the commitment amount
                this.company?.capitalMembers?.push({
                    memberType: CapitalMemberType.MainFund, // TODO - how do we know this is main fund?
                    name: commitmentMember.name,
                    percentage: 0,
                    commitment: commitmentMember.amount,
                    fundedToDate: 0,
                    distributedToDate:0
                })
            }else{
                capitalMember.commitment = commitmentMember.amount
            }
        }
    }

    
    xirr(finalDate: Date = new Date(), fundName?: string, fundSplitIRRPC?: boolean, holdingValueOverride?: number): number {
        const dateOnly = (d: Date) =>
            new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));

        const finalDateOnly = dateOnly(finalDate);

        let investedCapital:any[] = [];
        let distributions:any[] = [];
        let residualAmount = 0;

        // Fund level
        if ((fundName && this.company?.splitLogicEnabled) || (fundName && fundSplitIRRPC)) {
            const ratio = this.fundRatio(fundName, finalDateOnly);
            const fundCreatedDate = this.fundCreatedDate(fundName);
            const isCapCallAvailable = this.company.investedCapital?.some(cc => cc.state === CapitalCallState.Funded && cc.capitalCallType === CapitalCallType.EquityCall && cc.fundingDate >= fundCreatedDate);
            if(isCapCallAvailable){
                investedCapital = (this.company.investedCapital || [])
                .filter(cc => cc.state === CapitalCallState.Funded && cc.capitalCallType === CapitalCallType.EquityCall && cc.fundingDate >= fundCreatedDate)
                .map(c => {
                    let amountForFund = 0;
                    if (c.capitalContributions && c.capitalContributions.length > 0) {
                        amountForFund = c.capitalContributions.reduce((sum, contrib) => {
                            if (!contrib || !contrib.member) return sum;
                            const members = Array.isArray(contrib.member) ? contrib.member : [contrib.member];
                            for (const m of members) {
                                if (m?.name && m.name.toLowerCase() === fundName.toLowerCase()) {
                                    return sum + (contrib.amount || 0);
                                }
                            }
                            return sum;
                        }, 0);
                    } else {
                        amountForFund = c.amountQuantum  * ratio;
                    }
                    return { date: dateOnly(c.fundingDate), amount: -amountForFund };
                });
            }
            

            const isDistributionAvailable = this.company.distributions?.some(c => c.state === DistributionState.Funded && c.fundingDate >= fundCreatedDate);
            if(isDistributionAvailable){
            distributions = (this.company.distributions || [])
                .filter(c => c.state === DistributionState.Funded && c.fundingDate >= fundCreatedDate)
                .map(c => {
                    let amountForFund = 0;
                    if (c.distributionContributions && c.distributionContributions.length > 0) {
                        amountForFund = c.distributionContributions.reduce((sum, contrib) => {
                            if (!contrib || !contrib.member) return sum;
                            const members = Array.isArray(contrib.member) ? contrib.member : [contrib.member];
                            for (const m of members) {
                                if (m?.name && m.name.toLowerCase() === fundName.toLowerCase()) {
                                    return sum + (contrib.amount || 0);
                                }
                            }
                            return sum;
                        }, 0);
                    } else {
                        amountForFund = c.amountQuantum  * ratio;
                    }
                    return { date: dateOnly(c.fundingDate), amount: amountForFund };
                });
            }
          
            residualAmount = holdingValueOverride != null ? holdingValueOverride : this.holdingValue() * ratio;
        }

        // COMPANY-LEVEL
        else {
            investedCapital = (this.company.investedCapital || [])
                .filter(cc => cc.state === CapitalCallState.Funded && cc.capitalCallType === CapitalCallType.EquityCall)
                .map(c => ({
                    date: dateOnly(c.fundingDate),
                    amount: -c.amountQuantum
                }));

            distributions = (this.company.distributions || [])
                .map(c => ({
                    date: dateOnly(c.fundingDate),
                    amount: c.amountQuantum
                }));

            residualAmount = holdingValueOverride != null ? holdingValueOverride : this.holdingValue();
        }

        // Residual cashflow
        const residual = {
            date: finalDateOnly,
            amount: residualAmount
        };

        // Combine all cashflows
        const cashflows = [...investedCapital, ...distributions, residual]
            .filter(c => c && c.amount !== undefined && c.date)
            .sort((a, b) => a.date.getTime() - b.date.getTime());
        if (cashflows.length === 0) return NaN;        
        try {
            const retVal = finance.XIRR(cashflows.map(c => c.amount), cashflows.map(c => c.date));
            if (!Number.isFinite(retVal)) {
                return NaN;
            }

            return retVal/100;
        } catch (e) {
            return NaN;
        }
    }
    /**
     * Returns the earliest effective date when the specified fund
     * received an approved commitment.
     *
     * - Filters commitments with state "Approved"
     * - Checks if the fund exists in the commitment members
     * - Sorts by effectiveDate in ascending order
     * - Returns the earliest effectiveDate found
     *
     * @param fundName - Name of the fund
     * @returns The fund creation date, or null if not found
     */
    fundCreatedDate(fundName: string): Date | null {
      if(!fundName) return null;
      return  this.company.commitments.filter(commitment=>commitment.state === CommitmentState.Approved 
        && commitment.members.some(m => m.name.toLocaleLowerCase() === fundName.toLocaleLowerCase()))
        .sort((a, b) => a.effectiveDate < b.effectiveDate ? -1 : 1)[0]?.effectiveDate
    }

/**
 * Calculates the total realized value for a given fund as of a specific date.
 *
 * - Considers only distributions with state "Funded"
 * - Includes distributions with fundingDate <= asOf
 * - If distributionContributions exist, sums only the amounts
 *   allocated to the specified fund
 * - If no distributionContributions split exists, allocates value
 *   proportionally using the fund ratio
 *
 * @param fundName - Name of the fund
 * @param asOf - Cutoff date for calculation (defaults to today)
 * @returns Total realized value for the fund
 */

    fundRealizedValue(fundName:string, asOf: Date = new Date()): number {
        const ratio = this.fundRatio(fundName, asOf);
        let realizedValue = 0;
        (this.company.distributions || [])                
        .filter(c => c.state === DistributionState.Funded && c.fundingDate <= asOf)
                .map(c => {
                    if (c.distributionContributions && c.distributionContributions.length > 0) {
                        realizedValue += c.distributionContributions.reduce((sum, contrib) => {
                            if (!contrib || !contrib.member) return sum;
                            const members = Array.isArray(contrib.member) ? contrib.member : [contrib.member];
                            for (const m of members) {
                                if (m?.name && m.name.toLowerCase() === fundName.toLowerCase()) {
                                    return sum + (contrib.amount || 0);
                                }
                            }
                            return sum;
                        }, 0);
                    } else {
                        realizedValue += c.amountQuantum  * ratio;
                    }
                });
        return realizedValue;
    }


}

/**
 * When creating a 'trimmed' calculator, we need to check the valuation state
 * against the asOfDate, which is the date that we are trimming to. We want to
 * find any valuation records that are in a state that is valid for the asOfDate.
 * 
 * @param c A valuation record
 * @param asOfDate The date to check against
 * @returns true if the valuation record is in a state that is valid for the asOfDate
 */

function isSameDate(d1: Date, d2: Date) {
    return d1.getUTCFullYear() === d2.getUTCFullYear() &&
           d1.getUTCMonth() === d2.getUTCMonth() &&
           d1.getUTCDate() === d2.getUTCDate();
}

function checkValuationState(c: IValuation, asOfDate: Date): boolean {
    if (!c || !c.effectiveDate || !asOfDate) return false;

    let effDate: Date;
    try {
        effDate = c.effectiveDate instanceof Date ? c.effectiveDate : new Date(c.effectiveDate);
    } catch {
        return false;
    }

    if (isNaN(effDate.getTime())) {        
        return false;
    }

    const sameDay = isSameDate(effDate, asOfDate);

    if (sameDay) return true;
    if (effDate <= asOfDate && c.state === ValuationState.Submitted) return true;

    return false;
}

export default CompanyRecalcService;
